import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Baby, Smile, Users, GraduationCap } from "lucide-react";
import infantImage from "@/assets/programs/infant-card.jpg";
import earlyToddlerImage from "@/assets/programs/early-toddler-card.jpg";
import middleToddlerImage from "@/assets/programs/middle-toddler-card.jpg";
import olderToddlerImage from "@/assets/programs/older-toddler-card.jpg";
import prePreschoolImage from "@/assets/programs/pre-preschool-card.jpg";
import preschoolImage from "@/assets/programs/preschool-card.jpg";
import transitionalKImage from "@/assets/programs/transitional-k-card.jpg";
import preparatoryKImage from "@/assets/programs/preparatory-kindergarten.jpg";
import kindergartenImage from "@/assets/programs/kindergarten.jpg";

const ProgramsSection = () => {
  const programs = [
    {
      icon: Baby,
      title: "Infant Program",
      age: "3-12 months",
      description: "Welcome to our RIE-inspired infant room, a calm, loving space where your baby is known, seen, and cared for in a gentle, unhurried manner. RIE means we slow down, talk through care, and follow your child's cues. Care moments (feeding, diapering, cuddling) are one-on-one and never rushed. Babies play on the floor with safe, simple materials, discovering at their own pace; we don't prop or push.",
      color: "primary",
      image: infantImage,
      link: "/infant-program"
    },
    {
      icon: Smile,
      title: "Early Toddler Program",
      age: "13-18 months",
      description: "Welcome to our RIE-inspired early toddler room: a gentle, home-like space for new walkers and brave first goodbyes. We speak respectfully, follow cues, and take care unhurriedly. Play is simple, safe, and on the floor; we practice a few helpful signs, songs, and soft climbing. Fresh-air walks in our commercial buggy in light Oregon rain or play indoors in our gym or outdoors in our wonderful playground.",
      color: "secondary",
      image: earlyToddlerImage,
      link: "/early-toddler-program"
    },
    {
      icon: Smile,
      title: "Middle Toddler Program",
      age: "18-24 months",
      description: "Welcome to our RIE-inspired middle toddler room, where language blossoms and first friendships grow. We honor big feelings with calm voices, clear choices, and steady routines. Children explore sensory play, art, music, and simple problem-solving at their own pace. Outdoor play in light rain; on wetter days, we enjoy movement in our regulation-size gym. You'll receive daily updates and photos of your child's development, delivered in a friendly, personal way.",
      color: "secondary",
      image: middleToddlerImage,
      link: "/middle-toddler-program"
    },
    {
      icon: Smile,
      title: "Older Toddler Program",
      age: "24-30 months",
      description: "Welcome to our RIE-inspired older toddler room, confident helpers on the gentle bridge to pre-preschool. We invite cooperation, kindness, and self-help through real tasks, dramatic play, early counting, and hands-on discovery. Outdoor play in light rain; on soaked days, we head to our regulation-size gym. With familiar teachers and predictable rhythms, children grow secure, capable, and proud, while you stay connected through daily notes and photos.",
      color: "secondary",
      image: olderToddlerImage,
      link: "/older-toddler-program"
    },
    {
      icon: Users,
      title: "Pre-Preschool Program",
      age: "30-36 months",
      description: "Welcome to our Pre-Preschool room, a gentle bridge from toddlers to preschool. We place a strong emphasis on nurturing independence and fostering exploration; this is achieved through calm routines, rich language, and simple responsibilities. Children explore STEAM every day through activities such as tinkering, building, drawing, sorting, pouring, and observing how things work. We follow your child's pace with kindness and clarity. Fresh air in light Oregon rain; playground play on brighter days, and when the weather prevents outdoor play, our regulation-size gym provides a much-needed exercise alternative.",
      color: "accent",
      image: prePreschoolImage,
      link: "/pre-preschool-program"
    },
    {
      icon: Users,
      title: "Preschool Program",
      age: "36-48 months",
      description: "Welcome to our Preschool room! Here, imagination blooms and little skills grow in a warm, family-like environment. Kids dive into delightful stories and have fun with early writing, number games, nature explorations, music, and art. Our Developmentally Appropriate Curriculum weaves in STEAM learning in a way that feels like play! We value kindness, respect, problem-solving, and teamwork, and our caring teachers provide gentle guidance through our calm and consistent routines. We love spending time outdoors, even in light rain, and we enjoy exciting adventures on the playground.",
      color: "accent",
      image: preschoolImage,
      link: "/preschool-program"
    },
    {
      icon: GraduationCap,
      title: "Transitional Kindergarten Program",
      age: "48-59 months",
      // Patricia's wording, 14 Sep 2026, to match the revised program page.
      description: "Flexible scheduling meets joyful kindergarten preparation through research-based literacy, hands-on math and science, STEAM experiences, purposeful play, and outdoor exploration.",
      color: "tertiary",
      image: transitionalKImage,
      link: "/prekindergarten-program"
    },
    {
      icon: GraduationCap,
      title: "Preparatory Kindergarten Program",
      age: "4.5-5 years",
      // Patricia's wording, 14 Sep 2026, to match the revised program page.
      description: "A five-day kindergarten-preparation program where literacy, math, science, STEAM experiences, and social-emotional learning build with continuity and depth.",
      color: "tertiary",
      image: preparatoryKImage,
      link: "/preparatory-kindergarten-program"
    },
    {
      icon: GraduationCap,
      title: "Kindergarten Program",
      age: "5-6 years",
      description: "Welcome to our Kindergarten classroom! Warm, purposeful, and joyful, our educated teacher leads a true 1:15 class, delivering teacher-designed, STEAM-integrated lessons that exceed Oregon standards and align with national gold standards. Children build strong reading and writing skills, develop rich vocabulary, and learn the author's craft, while also gaining math fluency and engaging in science investigations. They begin learning D'Nealian cursive in a caring, family-style class where character and confidence grow. The daily rhythm balances mini-lessons, small groups, independent practice, and outdoor learning. We pre-assess and scaffold students up to Grade 1 targets as skills are mastered (leveled reading groups, accelerated number sense and problem-solving, weekly STEAM inquiry). You'll receive Weekly Go Notes and photos, plus ongoing assessment with clear next steps.",
      color: "tertiary",
      image: kindergartenImage,
      link: "/kindergarten-program"
    }
  ];

  return (
    <section id="programs" className="py-10 programs-gradient-overlay relative" style={{ background: '#2C003E', scrollMarginTop: '194px' }}>
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="font-header text-3xl md:text-4xl font-bold text-white mb-4">
            Our Programs
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Thoughtfully designed programs that blend academic excellence with joyful discovery, ensuring every child thrives.
          </p>
        </div>
        
        {/* Capped at 3 columns so the nine programs land as an even 3x3 (a 4-wide
            grid left a ragged 4/4/1). Max width keeps the cards from stretching
            on very wide screens. */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {programs.map((program, index) => (
            <Link to={program.link} key={index}>
              <Card 
                className="overflow-hidden rounded-3xl bg-[#fffdd0] hover:shadow-large transition-all hover:-translate-y-1 animate-fade-in-up border-0 group h-full flex flex-col"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={program.image} 
                    alt={`${program.title} classroom`}
                    loading="lazy"
                    decoding="async"
                    width={1248}
                    height={832}
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${
                      (index === 7 || index === 8) ? 'object-[center_40%]' : index === 9 ? 'object-[center_30%]' : ''
                    }`}
                  />
                </div>
                
                <div className="p-6 flex flex-col flex-grow">
                  <div className="mb-3">
                    <Badge variant="outline" className="mb-2 text-xs border-[#5E2DB4] text-[#5E2DB4]">{program.age}</Badge>
                    <h3 className="font-header font-semibold text-xl text-[#2C003E]">{program.title}</h3>
                  </div>
                  
                  <p className="text-[#2C003E]/80 text-sm leading-relaxed mb-4 flex-grow">
                    {program.description}
                  </p>
                  
                  <Button variant="program" className="w-full h-auto min-h-[48px] mt-auto py-3">
                    <span className="flex flex-col items-center gap-1 w-full">
                      <span className="block text-center w-full">Press the button to learn more about our</span>
                      <span className="block text-center w-full">{program.title}</span>
                    </span>
                  </Button>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProgramsSection;
